import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SMS_SENDER_FORM_FIELD_NAMES, SmsSender, SmsSenderType } from '../../types';

const { NAME, PHONE_NUMBER, MESSAGE_TYPE_ID, ESP_ACCOUNT_ID, TYPE } = SMS_SENDER_FORM_FIELD_NAMES;

export const smsSenderSchema = z.object({
  [NAME]: z.string().min(1, { message: 'Enter a name' }),
  [PHONE_NUMBER]: z.string().min(1, { message: 'Enter a value' }),
  [MESSAGE_TYPE_ID]: z.number().nullable(),
  [ESP_ACCOUNT_ID]: z.number(),
  [TYPE]: z.nativeEnum(SmsSenderType),
});

export type SmsSenderSchema = z.infer<typeof smsSenderSchema>;

const emptyValues = (espAccountId: number) => ({
  [NAME]: '',
  [PHONE_NUMBER]: '',
  [MESSAGE_TYPE_ID]: null,
  [ESP_ACCOUNT_ID]: espAccountId,
  [TYPE]: SmsSenderType.MESSAGING_SERVICE,
});

const createInitialValues = (smsSender: SmsSender) => ({
  [NAME]: smsSender.name,
  [PHONE_NUMBER]: smsSender.phoneNumber?.slice(-4).padStart(20, '*'),
  [MESSAGE_TYPE_ID]: smsSender.messageTypeId ?? 0,
  [ESP_ACCOUNT_ID]: smsSender.espAccountId,
  [TYPE]: SmsSenderType.MESSAGING_SERVICE, // Type always have the same value
});

export const SmsSenderFormProvider: FC<
  React.PropsWithChildren<{ defaultValues: SmsSenderSchema }>
> = ({ children, defaultValues }) => {
  const methods = useForm<SmsSenderSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(smsSenderSchema),
  });
  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const CreateSmsSenderFormProvider: FC<React.PropsWithChildren<{ espAccountId: number }>> = ({
  espAccountId,
  ...props
}) => <SmsSenderFormProvider defaultValues={emptyValues(espAccountId)} {...props} />;

export const EditSmsSenderFormProvider: FC<React.PropsWithChildren<{ smsSender: SmsSender }>> = ({
  smsSender,
  ...props
}) => <SmsSenderFormProvider defaultValues={createInitialValues(smsSender)} {...props} />;
